import { useCallback, useMemo, useState } from "react";
import type { FormEvent } from "react";
import {
  Alert,
  Button,
  Group,
  Modal,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  legacyReportCorrectionService,
  type LegacyCorrectableHeader,
  type LegacyReportCorrectionBody,
} from "../../../services/legacyReportCorrection.service";
import type { DirectCorrectionPreviewResponse } from "../../../services/patientInfoService";
import type {
  ReportContentVersion,
  ReportPreviewEvidence,
} from "../../../services/signOff.service";
import {
  createLegacyCorrectionBody,
  createLegacyCorrectionValues,
  LEGACY_CORRECTION_FIELDS,
} from "../../../utils/legacyReportCorrection";
import { ReportPreviewModal } from "../../workflows/ReportPreviewModal";

interface Props {
  caseId: string;
  header: LegacyCorrectableHeader;
  onClose: () => void;
}

export default function LegacyReportCorrectionEditor({
  caseId,
  header,
  onClose,
}: Props) {
  const client = useQueryClient();
  const [preview, setPreview] =
    useState<DirectCorrectionPreviewResponse | null>(null);
  const [body, setBody] = useState<LegacyReportCorrectionBody | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [previewOpened, setPreviewOpened] = useState(false);
  const form = useForm({
    initialValues: createLegacyCorrectionValues(header),
    validateInputOnBlur: true,
    validate: {
      reason: (value: string) =>
        value.trim() ? null : "Enter the reason for the report correction.",
    },
  });
  const previewData = useMemo(
    () =>
      preview ? { ...preview.previewData, review: preview.review } : undefined,
    [preview]
  );
  const fields = useMemo(
    () =>
      LEGACY_CORRECTION_FIELDS.filter(
        ([field]) =>
          !header.patientNameText ||
          (field !== "patientFirstName" && field !== "patientLastName")
      ),
    [header.patientNameText]
  );
  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (pending) return;
      const validation = form.validate();
      if (validation.hasErrors) {
        const node = form.getInputNode(Object.keys(validation.errors)[0]);
        node?.scrollIntoView?.({ block: "center" });
        node?.focus();
        return;
      }
      const proposed = createLegacyCorrectionBody(header, form.getValues());
      if (!proposed) {
        setError(
          "Change at least one historical report field before generating a corrected preview."
        );
        return;
      }
      setPending(true);
      setError(null);
      try {
        const result = await legacyReportCorrectionService.preview(
          caseId,
          proposed
        );
        setBody({
          ...proposed,
          correctionId: result.correctionId,
          correctionTimestamp: result.correctionTimestamp,
        });
        setPreview(result);
        setPreviewOpened(true);
      } catch (failure) {
        setError((failure as Error).message);
      } finally {
        setPending(false);
      }
    },
    [pending, form, header, caseId]
  );
  const handleBack = useCallback(() => setPreviewOpened(false), []);
  const handleAcknowledge = useCallback(
    async (contentVersion: ReportContentVersion) => {
      if (!body)
        throw new Error("Generate the corrected preview again before signing.");
      return legacyReportCorrectionService.acknowledge(
        caseId,
        body,
        contentVersion
      );
    },
    [body, caseId]
  );
  const handleRefreshPreview = useCallback(async () => {
    if (!body) return;
    const result = await legacyReportCorrectionService.preview(caseId, body);
    setBody({
      ...body,
      correctionId: result.correctionId,
      correctionTimestamp: result.correctionTimestamp,
    });
    setPreview(result);
  }, [body, caseId]);
  const handleApply = useCallback(
    async (previewEvidence?: ReportPreviewEvidence) => {
      if (!body || !previewEvidence || pending) return;
      setPending(true);
      try {
        const result = await legacyReportCorrectionService.apply(caseId, {
          ...body,
          previewEvidence,
        });
        if (!result.success)
          throw new Error(
            "The corrected report was not issued. Review the preview and try again."
          );
        await Promise.all(
          [
            "case",
            "legacy-import",
            "case-for-pathologist-review",
            "document-view",
            "report-preview",
            "sign-off-history",
            "legacy-report-correction-base",
          ].map((key) => client.invalidateQueries({ queryKey: [key, caseId] }))
        );
        notifications.show({
          color: "green",
          title: "Report correction issued",
          message:
            "The corrected historical identity is recorded in a new report version. The original and approved baseline are preserved.",
          autoClose: 6000,
        });
        onClose();
      } finally {
        setPending(false);
      }
    },
    [body, pending, caseId, client, onClose]
  );
  return (
    <>
      <Modal
        opened={!previewOpened}
        onClose={onClose}
        title="Correct historical report identity"
        size="xl"
        centered
        closeOnClickOutside={false}
      >
        <form onSubmit={handleSubmit} noValidate>
          <Stack gap="md">
            <ScrollArea.Autosize mah="60vh">
              <Stack gap="md">
                <Alert color="blue">
                  This correction creates a new report version with current
                  attribution. It changes the report's historical identity
                  fields only; the linked patient record stays unchanged.
                </Alert>
                {error && (
                  <Alert color="red" title="Correction needs attention">
                    {error}
                  </Alert>
                )}
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  {fields.map(([field, label]) => (
                    <TextInput
                      key={field}
                      label={label}
                      description={
                        field === "patientNameText" && header.patientNameText
                          ? "This is the name printed on the historical report."
                          : undefined
                      }
                      disabled={pending}
                      {...form.getInputProps(`header.${field}`)}
                    />
                  ))}
                </SimpleGrid>
                <Textarea
                  label="Reason for correction"
                  required
                  autosize
                  minRows={3}
                  disabled={pending}
                  {...form.getInputProps("reason")}
                />
                <Text size="sm" c="dimmed">
                  Keep source date wording and precision. Review the complete
                  corrected report before confirming it.
                </Text>
              </Stack>
            </ScrollArea.Autosize>
            <Group justify="space-between">
              <Button variant="default" onClick={onClose} disabled={pending}>
                Cancel
              </Button>
              <Button type="submit" loading={pending}>
                Review corrected report
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
      {previewOpened && preview && (
        <ReportPreviewModal
          key={preview.review.contentVersion.fingerprint}
          opened
          caseId={caseId}
          onClose={handleBack}
          onBack={handleBack}
          title="Review corrected historical report"
          documentViewDataOverride={preview.documentViewData}
          previewDataOverride={previewData}
          forceTemplateId={preview.templateId || undefined}
          reviewRequired
          allowSignOutWhenFinalized
          showPrintAction={false}
          signOutLabel="Confirm historical report correction"
          signOutPending={pending}
          onAcknowledgeReview={handleAcknowledge}
          onReviewContentChanged={handleRefreshPreview}
          onSignOut={handleApply}
        />
      )}
    </>
  );
}
